// LLM generated - Model Claude 4 Sonnet
import { useInfiniteQuery } from '@tanstack/react-query';
import { getMessages } from '../live-activity-service.service.api';
import type { GetMessagesQuery } from '../types';
import { createQueryKey } from '~/domains/contest/domains/gamecenter/domains/common/components/ContestChat/constants/queryKeys';

type UseChatMessagesInfiniteProps = {
  chatId?: string;
  enabled?: boolean;
  pageSize?: number;
  reversePageOrder?: boolean;
};

export const useChatMessagesInfinite = ({
  chatId,
  enabled = true,
  pageSize = 50,
  reversePageOrder = false,
}: UseChatMessagesInfiniteProps) => {
  const data = useInfiniteQuery({
    // Only create a valid query key when chatId is defined
    queryKey: chatId
      ? createQueryKey.chatMessages(chatId, pageSize, reversePageOrder)
      : ['chatMessages', 'disabled', pageSize, reversePageOrder],
    queryFn: async ({ pageParam }) => {
      // Safety check - if somehow called without chatId, return empty result
      // This can happen if refetch() is called before chatId is available
      if (!chatId) {
        return { data: [], hasNextPage: false, totalCount: 0 };
      }

      const query: GetMessagesQuery = {
        pageSize,
        reversePageOrder,
        ...(pageParam && { lastMessageId: pageParam }),
      };

      return getMessages(chatId, query);
    },
    // Only run query when chatId is defined
    enabled: enabled && !!chatId,
    staleTime: 2 * 60 * 1000, // 2 minutes - rely on Pusher for real-time updates
    refetchOnMount: true, // Load initial messages on mount
    refetchOnWindowFocus: false, // Don't refetch on focus - rely on Pusher invalidation
    refetchOnReconnect: true, // Only refetch when reconnecting after network issues
    getNextPageParam: (lastPage) => {
      // If there's no next page, return undefined to stop pagination
      if (!lastPage.hasNextPage) {
        return undefined;
      }

      // Return the ID of the last message to use as lastMessageId for the next page
      const lastMessage = lastPage.data[lastPage.data.length - 1];
      return lastMessage?.id;
    },
  });

  // Flatten all pages into a single array of messages
  const messages = data.data?.pages.flatMap((page) => page.data) ?? [];

  return {
    ...data,
    messages,
    totalCount: data.data?.pages[0]?.totalCount ?? 0,
  };
};
